import { useLiveQuery } from '@electric-sql/pglite-react';
import { isFinalLike } from '@quickreport/domain';
import { Badge, Button } from '@quickreport/ui';
import {
  ageFor,
  type PatientListItem,
  patientListQuery,
  type ReportListItem,
  reportListQuery,
} from '@quickreport/workspace';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { ArrowLeft, Download, FilePlus2, Pencil, Printer, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useWorkspace } from '../../lib/workspace';
import { STATUS_LABEL, STATUS_TONE } from '../reports/context';
import { downloadPdfs, makePdfs, PRINTABLE, printMany } from '../reports/print-many';
import { DeletePatientDialog } from './DeletePatientDialog';
import { EditPatient } from './PatientsPage';

const day = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/**
 * One patient: their details, and every report they have, in rows. A row opens the report; the
 * boxes pick several to download or print together.
 */
export function PatientDetailPage() {
  const { patientId } = useParams({ from: '/patients/$patientId' });
  const ws = useWorkspace();
  const navigate = useNavigate();
  const canEdit = ws.can('register_visit');

  const one = patientListQuery(ws.orgId, { id: patientId, pageSize: 1 });
  const patientRes = useLiveQuery<PatientListItem>(one.sql, one.params);
  const patient = patientRes?.rows[0];

  const list = reportListQuery(ws.orgId, {
    patientId,
    sort: 'collected',
    order: 'desc',
    page: 1,
    pageSize: 500,
  });
  const reportsRes = useLiveQuery<ReportListItem>(list.sql, list.params);
  const reports = useMemo(() => reportsRes?.rows ?? [], [reportsRes]);

  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const printable = reports.filter((r) => PRINTABLE.has(r.status));
  const chosen = [...picked].filter((id) => printable.some((r) => r.id === id));
  const allOn = printable.length > 0 && chosen.length === printable.length;

  const toggle = (id: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  async function run(ids: string[], what: 'download' | 'print') {
    if (ids.length === 0) return;
    setNote(null);
    try {
      setBusy('Preparing…');
      const files = await makePdfs(
        ws.db,
        ws.orgId,
        ws.auth.orgName,
        ws.auth.verifyBaseUrl,
        ids,
        (d, t) => setBusy(t > 1 ? `Preparing ${Math.min(d + 1, t)} of ${t}…` : 'Preparing…'),
      );
      if (files.length === 0)
        throw new Error('Those reports could not be opened on this computer.');
      if (what === 'download') {
        setBusy(files.length > 1 ? `Saving ${files.length} files…` : 'Saving…');
        await downloadPdfs(files);
        setNote({
          tone: 'ok',
          text:
            files.length === 1
              ? 'Saved.'
              : `Saved ${files.length} PDF files. If your browser asked to allow several downloads, choose Allow.`,
        });
      } else {
        setBusy(files.length > 1 ? `Printing ${files.length} reports…` : 'Printing…');
        const r = await printMany(files);
        if (!r.ok) throw new Error(r.error ?? 'Could not print.');
      }
    } catch (err) {
      setNote({ tone: 'error', text: err instanceof Error ? err.message : 'That did not work.' });
    } finally {
      setBusy(null);
    }
  }

  if (patientRes && !patient) {
    return (
      <div className="mx-auto max-w-4xl p-4 sm:p-8">
        <p>This patient is not on this computer.</p>
        <Link to="/patients" className="text-brand hover:underline">
          Back to patients
        </Link>
      </div>
    );
  }
  if (!patient) return <div className="p-8 text-slate">Opening patient…</div>;

  const age = ageFor(patient).ageText;
  const sexName =
    patient.sex === 'M'
      ? 'Male'
      : patient.sex === 'F'
        ? 'Female'
        : patient.sex === 'O'
          ? 'Other'
          : '';
  const fullName = [patient.salutation ? `${patient.salutation}.` : '', patient.name]
    .filter(Boolean)
    .join(' ');
  const details: [string, string][] = [
    ['Age / sex', [age, sexName].filter(Boolean).join(' · ') || '—'],
    ['Mobile', patient.phone || '—'],
    ['Email', patient.email || '—'],
    ['Address', patient.address || '—'],
    ['Patient ID', patient.external_id || '—'],
    ['Added', day(patient.created_at)],
  ];

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-8">
      <div className="flex items-center gap-2">
        <Link
          to="/patients"
          className="rounded p-1.5 text-slate hover:bg-canvas"
          aria-label="Back to patients"
        >
          <ArrowLeft size={18} />
        </Link>
        <h1 className="min-w-0 flex-1 truncate text-xl font-semibold">{fullName}</h1>
        {canEdit ? (
          <>
            <Button size="sm" onClick={() => setEditing(true)}>
              <Pencil size={14} /> Edit
            </Button>
            {ws.can('delete_signed') || !reports.some((r) => isFinalLike(r.status)) ? (
              <Button size="sm" tone="danger" onClick={() => setDeleting(true)}>
                <Trash2 size={14} /> Delete
              </Button>
            ) : null}
            <Link
              to="/reports/new"
              search={{ patient: patient.id }}
              className="inline-flex h-7 items-center gap-1.5 rounded-lg bg-navy px-2 text-[13px] font-medium text-white hover:bg-navy-hover"
            >
              <FilePlus2 size={14} /> New report
            </Link>
          </>
        ) : null}
      </div>

      <dl className="mt-4 grid gap-x-6 gap-y-3 rounded-lg border border-line bg-paper p-4 sm:grid-cols-2 lg:grid-cols-3">
        {details.map(([k, v]) => (
          <div key={k} className="min-w-0">
            <dt className="text-[12px] text-slate">{k}</dt>
            <dd className="break-words">{v}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-base font-semibold">
          Reports <span className="font-normal text-slate">({reports.length})</span>
        </h2>
        <Button
          size="sm"
          disabled={chosen.length === 0 || busy !== null}
          onClick={() => void run(chosen, 'download')}
        >
          <Download size={14} /> Download{chosen.length ? ` (${chosen.length})` : ''}
        </Button>
        <Button
          size="sm"
          tone="primary"
          disabled={chosen.length === 0 || busy !== null}
          onClick={() => void run(chosen, 'print')}
        >
          <Printer size={14} /> Print{chosen.length ? ` (${chosen.length})` : ''}
        </Button>
      </div>
      {busy ? (
        <p role="status" className="mt-2 text-[13px] text-slate">
          {busy}
        </p>
      ) : null}
      {note ? (
        <p
          role={note.tone === 'error' ? 'alert' : 'status'}
          className={clsx('mt-2 text-[13px]', note.tone === 'error' ? 'text-flag' : 'text-slate')}
        >
          {note.text}
        </p>
      ) : null}

      {reports.length === 0 ? (
        <p className="mt-3 rounded-lg border border-dashed border-line bg-paper px-4 py-10 text-center text-slate">
          No reports for this patient yet.
        </p>
      ) : (
        <>
          <ul className="mt-3 grid gap-2 md:hidden" aria-label="Reports of this patient">
            {reports.map((r) => (
              // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users open it with the report name link inside
              <li
                key={r.id}
                onClick={() =>
                  void navigate({ to: '/reports/$reportId', params: { reportId: r.id } })
                }
                className={clsx(
                  'flex cursor-pointer items-start gap-3 rounded-xl border border-line bg-paper p-3 active:bg-brand-soft/50',
                  picked.has(r.id) && 'bg-brand-soft/40',
                )}
              >
                {PRINTABLE.has(r.status) ? (
                  // biome-ignore lint/a11y/useKeyWithClickEvents: the buttons inside are the keyboard controls
                  // biome-ignore lint/a11y/noStaticElementInteractions: keeps the tick box from opening the report
                  <span onClick={(e) => e.stopPropagation()} className="pt-0.5">
                    <input
                      type="checkbox"
                      className="h-5 w-5 accent-brand"
                      checked={picked.has(r.id)}
                      onChange={() => toggle(r.id)}
                      aria-label={`Select ${r.lab_no}`}
                    />
                  </span>
                ) : (
                  <span className="w-5" />
                )}
                <div className="min-w-0 flex-1">
                  <Link
                    to="/reports/$reportId"
                    params={{ reportId: r.id }}
                    onClick={(e) => e.stopPropagation()}
                    className="block leading-snug font-medium text-ink"
                  >
                    {r.name}
                  </Link>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[12px] text-slate">
                    <span className="font-mono">{r.lab_no}</span>
                    <span>{day(r.collected_at ?? r.updated_at)}</span>
                    <Badge tone={STATUS_TONE[r.status] ?? 'neutral'}>
                      {STATUS_LABEL[r.status] ?? r.status}
                    </Badge>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-3 hidden overflow-hidden rounded-lg border border-line bg-paper md:block">
            <table className="w-full text-left">
              <thead className="bg-canvas text-[12px] tracking-wide text-slate uppercase">
                <tr>
                  <th className="w-10 px-4 py-2">
                    <input
                      type="checkbox"
                      className="accent-brand"
                      aria-label="Select all signed reports"
                      checked={allOn}
                      disabled={printable.length === 0}
                      onChange={() =>
                        setPicked(allOn ? new Set() : new Set(printable.map((r) => r.id)))
                      }
                    />
                  </th>
                  <th className="px-4 py-2 font-medium">Lab no.</th>
                  <th className="px-4 py-2 font-medium">Report</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Collected</th>
                  <th className="w-24 px-2 py-2">
                    <span className="sr-only">Download and print</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => {
                  const can = PRINTABLE.has(r.status);
                  return (
                    // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users open it with the report name link inside
                    <tr
                      key={r.id}
                      onClick={() =>
                        void navigate({ to: '/reports/$reportId', params: { reportId: r.id } })
                      }
                      className={clsx(
                        'cursor-pointer border-t border-line hover:bg-brand-soft/50',
                        picked.has(r.id) && 'bg-brand-soft/40',
                      )}
                    >
                      {/* biome-ignore lint/a11y/useKeyWithClickEvents: the buttons inside are the keyboard controls */}
                      {/* biome-ignore lint/a11y/noStaticElementInteractions: keeps the tick box and buttons from opening the report */}
                      <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                        {can ? (
                          <input
                            type="checkbox"
                            className="accent-brand"
                            checked={picked.has(r.id)}
                            onChange={() => toggle(r.id)}
                            aria-label={`Select ${r.lab_no}`}
                          />
                        ) : null}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[13px] text-slate">{r.lab_no}</td>
                      <td className="px-4 py-2.5 font-medium">
                        <Link
                          to="/reports/$reportId"
                          params={{ reportId: r.id }}
                          onClick={(e) => e.stopPropagation()}
                          className="text-ink hover:text-brand"
                        >
                          {r.name}
                        </Link>
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge tone={STATUS_TONE[r.status] ?? 'neutral'}>
                          {STATUS_LABEL[r.status] ?? r.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-[13px] text-slate">
                        {day(r.collected_at ?? r.updated_at)}
                      </td>
                      {/* biome-ignore lint/a11y/useKeyWithClickEvents: the buttons inside are the keyboard controls */}
                      {/* biome-ignore lint/a11y/noStaticElementInteractions: keeps the buttons from opening the report */}
                      <td className="px-2 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                        {can ? (
                          <span className="inline-flex gap-1">
                            <button
                              type="button"
                              title="Download this report"
                              aria-label={`Download ${r.lab_no}`}
                              disabled={busy !== null}
                              onClick={() => void run([r.id], 'download')}
                              className="grid h-8 w-8 place-items-center rounded-lg text-navy hover:bg-brand-soft disabled:opacity-50"
                            >
                              <Download size={15} />
                            </button>
                            <button
                              type="button"
                              title="Print this report"
                              aria-label={`Print ${r.lab_no}`}
                              disabled={busy !== null}
                              onClick={() => void run([r.id], 'print')}
                              className="grid h-8 w-8 place-items-center rounded-lg text-navy hover:bg-brand-soft disabled:opacity-50"
                            >
                              <Printer size={15} />
                            </button>
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {printable.length < reports.length ? (
            <p className="mt-2 text-[12px] text-slate">
              Only signed reports can be picked here. Open a report that is still a draft or being
              corrected to work on it.
            </p>
          ) : null}
        </>
      )}

      {deleting ? (
        <DeletePatientDialog
          patientId={patient.id}
          onDeleted={() => void navigate({ to: '/patients' })}
          onClose={() => setDeleting(false)}
        />
      ) : null}
      {editing ? (
        <EditPatient
          patient={patient}
          onClose={() => setEditing(false)}
          onSaved={() => void ws.engine.syncNow()}
        />
      ) : null}
    </div>
  );
}
